import { CustomTooltip } from "@shared/components/CustomTooltip";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Calendar, Circle, Wrench } from "lucide-react";

interface ActiveOperationsSectionProps {
  data: any;
}

export const ActiveOperationsSection = ({
  data,
}: ActiveOperationsSectionProps) => {
  const activeWorkOrders = data?.activeWorkOrders;
  const getPriorityVariant = (priority: string) => {
    switch (priority) {
      case "high":
        return "destructive";
      case "medium":
        return "outline";
      case "low":
        return "secondary";
      default:
        return "secondary";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return "text-status-good";
      case "in-progress":
        return "text-status-fair";
      case "overdue":
        return "text-status-poor";
      default:
        return "text-muted-foreground";
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              Active Work Orders
            </CardTitle>
            <CustomTooltip message="Currently open work orders showing priority, assignee, and estimated completion time. Essential for tracking ongoing maintenance activities." />
          </div>
          <p className="text-sm text-muted-foreground">
            {activeWorkOrders?.length} open orders
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {activeWorkOrders && activeWorkOrders.length > 0 ? (
              activeWorkOrders.map((order, index) => (
                <div
                  key={index}
                  className="flex justify-between items-start p-3 rounded-lg border"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge
                        variant={getPriorityVariant(order.priority)}
                        className="text-xs"
                      >
                        {order.priority}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {order.woNumber}
                      </span>
                    </div>
                    <p className="font-medium text-sm">{order.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {order.assignedTo} • {order.estimatedDuration}h
                    </p>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {order.dueDate ?? "NA"}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-sm text-muted-foreground">
                No active work orders.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Scheduled Maintenance
            </CardTitle>
            <CustomTooltip message="Upcoming preventive maintenance activities scheduled for the next 30 days, showing asset, type, and current status." />
          </div>
          <p className="text-sm text-muted-foreground">Next 30 days</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data?.scheduledMaintenance &&
            data.scheduledMaintenance.length > 0 ? (
              data.scheduledMaintenance.map((maintenance, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center p-3 rounded-lg border"
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {maintenance.assetName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {maintenance.type ?? "NA"} • {maintenance.assignedTeam}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">
                      {maintenance.dueDate}
                    </p>
                    <div className="flex items-center gap-1">
                      <Circle
                        className={`h-2 w-2 fill-current ${getStatusColor(
                          maintenance.status
                        )}`}
                      />
                      <span
                        className={`text-xs ${getStatusColor(
                          maintenance.status
                        )}`}
                      >
                        {maintenance.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-sm text-muted-foreground">
                No scheduled maintenance.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
